import { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import styled from 'styled-components';
import { ContractFilterInput, useGetContractsForListQuery } from '@/graphql/generated';
import { useUserLanguage } from '@/customHooks';
import { Pagination } from '../molecules';
import textsData from '@/textData/reportTexts.json';
import {
  MONTHS_ES,
  MONTHS_EN,
  initialContractRoadmapFilters,
} from '@/components/constants/contractRoadmapReport';

const TableWrapper = styled.div`
  margin-top: 1.5rem;
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  padding: 1rem 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const RoadmapTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th,
  td {
    padding: 0.4rem 0.5rem;
    font-size: 0.8rem;
    text-align: left;
    white-space: nowrap;
  }

  th {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.light};
    border-bottom: 1px solid ${({ theme }) => theme.colors.primary2};
    position: sticky;
    top: 0;
    background-color: ${({ theme }) => theme.colors.primary2};
    z-index: 1;
  }

  thead tr {
    background-color: ${({ theme }) => theme.colors.primary2};
  }

  tbody tr:nth-child(even) {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`;

const MonthsCell = styled.td`
  padding-top: 0.6rem;
  padding-bottom: 0.6rem;
`;

const MonthsBarTrack = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 2px;
  height: 28px;
`;

const MonthSlot = styled.div`
  background-color: ${({ theme }) => theme.colors.background2};
  border-radius: ${({ theme }) => theme.border.radius.sm};
`;

const MonthBar = styled.div<{ $start: number; $end: number }>`
  position: absolute;
  top: 4px;
  bottom: 4px;
  left: ${({ $start }) => `calc((100% / 12) * ${$start} + 1px)`};
  width: ${({ $start, $end }) =>
    $end >= $start ? `calc((100% / 12) * ${$end - $start + 1} - 2px)` : '0'};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.primary1},
    ${({ theme }) => theme.colors.primary2}
  );
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.65rem;
  background-color: ${({ theme }) => theme.colors.background2};
  color: ${({ theme }) => theme.colors.text};
`;

const SelectedContractPanel = styled.div`
  margin-top: 1rem;
  padding: 1rem 1.25rem;
  border-radius: ${({ theme }) => theme.border.radius.lg};
  background-color: ${({ theme }) => theme.colors.background2};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const SelectedTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: ${({ theme }) => theme.colors.text};
`;

const YearSelector = styled.select`
  padding: 0.35rem 0.75rem;
  border-radius: ${({ theme }) => theme.border.radius.base};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.light};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.text};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary2};
  }
`;

const ViewTabs = styled.div`
  display: inline-flex;
  gap: 0.25rem;
  padding: 0.2rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.background2};
`;

const ViewTab = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary1 : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.light : theme.colors.text};
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
`;

const UnitsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const UnitItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.6rem 0.75rem;
  border-radius: ${({ theme }) => theme.border.radius.base};
  background-color: ${({ theme }) => theme.colors.light};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const UnitName = styled.span`
  font-weight: 600;
`;

const UnitPeriod = styled.span`
  font-size: 0.8rem;
  opacity: 0.85;
`;

type RoadmapViewType = 'property' | 'unit';
type ContractItem = NonNullable<
  ReturnType<typeof useGetContractsForListQuery>['data']
>['contracts']['items'][number];

type BarPosition = {
  startMonthIndex: number | null;
  endMonthIndex: number | null;
};

const formatDate = (value: string | null | undefined, language: 'es' | 'en') => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const getBarPositionByYear = (
  startValue: string | null | undefined,
  finishValue: string | null | undefined,
  selectedYear: number
): BarPosition => {
  const contractStart = startValue ? new Date(startValue) : null;
  const contractFinish = finishValue ? new Date(finishValue) : null;

  if (!contractStart || Number.isNaN(contractStart.getTime())) {
    return { startMonthIndex: null, endMonthIndex: null };
  }

  const yearStart = new Date(selectedYear, 0, 1);
  const yearEnd = new Date(selectedYear, 11, 31);
  const effectiveStart = contractStart > yearStart ? contractStart : yearStart;
  const effectiveEndRaw =
    contractFinish && !Number.isNaN(contractFinish.getTime())
      ? contractFinish
      : yearEnd;
  const effectiveEnd = effectiveEndRaw < yearEnd ? effectiveEndRaw : yearEnd;

  if (effectiveEnd < yearStart || effectiveStart > yearEnd) {
    return { startMonthIndex: null, endMonthIndex: null };
  }

  return {
    startMonthIndex: effectiveStart.getMonth(),
    endMonthIndex: effectiveEnd.getMonth(),
  };
};

export const ContractsRoadmapReportPage = () => {
  const userLanguage = useUserLanguage();
  const months = userLanguage === 'es' ? MONTHS_ES : MONTHS_EN;
  const texts = (userLanguage === 'es' ? textsData.es : textsData.en).contractsRoadmapReport;

  const [currentPage, setCurrentPage] = useState(1);
  const [filters] = useState<ContractFilterInput>(initialContractRoadmapFilters);
  const [expandedPropertyId, setExpandedPropertyId] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [viewType, setViewType] = useState<RoadmapViewType>('property');

  const normalizedFilters: ContractFilterInput = useMemo(
    () => ({
      ...filters,
      page: currentPage.toString(),
    }),
    [filters, currentPage]
  );

  const { data } = useGetContractsForListQuery({
    variables: {
      input: normalizedFilters,
    },
    fetchPolicy: 'network-only',
  });

  const items = useMemo(() => data?.contracts?.items ?? [], [data?.contracts?.items]);
  const currentDataCount = items.length;
  const totalPages = data?.contracts?.pagination?.totalPages || 1;
  const showPagination = currentDataCount > 0;

  const groupedByProperty = useMemo(() => {
    const map = new Map<
      string,
      {
        propertyId: string;
        propertyName: string;
        contracts: ContractItem[];
      }
    >();

    items.forEach((contract) => {
      const propertyId = contract.property?.id ?? contract.id;
      const propertyName =
        contract.property?.name ??
        (userLanguage === 'es' ? 'Propiedad sin nombre' : 'Unnamed property');

      const existing = map.get(propertyId);
      if (existing) {
        existing.contracts.push(contract);
        return;
      }

      map.set(propertyId, {
        propertyId,
        propertyName,
        contracts: [contract],
      });
    });

    return Array.from(map.values());
  }, [items, userLanguage]);

  const years = useMemo(() => {
    const yearSet = new Set<number>();
    items.forEach((contract) => {
      if (contract.startDate) {
        const y = new Date(contract.startDate).getFullYear();
        if (!Number.isNaN(y)) yearSet.add(y);
      }
      if (contract.finishDate) {
        const y = new Date(contract.finishDate).getFullYear();
        if (!Number.isNaN(y)) yearSet.add(y);
      }
    });
    return Array.from(yearSet).sort((a, b) => a - b);
  }, [items]);

  useEffect(() => {
    if (years.length === 0) return;
    setSelectedYear((prev) => {
      if (prev !== null && years.includes(prev)) return prev;
      const current = new Date().getFullYear();
      if (years.includes(current)) return current;
      return years[0];
    });
  }, [years]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <h2>{texts.title}</h2>
        </Col>
        <Col xs="content">
          <ViewTabs>
            <ViewTab
              type="button"
              $active={viewType === 'property'}
              onClick={() => setViewType('property')}
            >
              {userLanguage === 'es' ? 'Por propiedad' : 'By property'}
            </ViewTab>
            <ViewTab
              type="button"
              $active={viewType === 'unit'}
              onClick={() => setViewType('unit')}
            >
              {userLanguage === 'es' ? 'Por unidad' : 'By unit'}
            </ViewTab>
          </ViewTabs>
        </Col>
        {years.length > 0 && selectedYear !== null && (
          <Col xs="content">
            <YearSelector
              value={selectedYear}
              onChange={(event) => setSelectedYear(Number(event.target.value))}
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </YearSelector>
          </Col>
        )}
      </Row>
      <Row>
        <Col>
          <TableWrapper>
            <RoadmapTable>
              <thead>
                <tr>
                  <th>{texts.columns.id}</th>
                  <th>{texts.columns.property}</th>
                  <th>{texts.columns.status}</th>
                  {months.map((month) => (
                    <th key={month}>{month}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selectedYear !== null &&
                  viewType === 'property' &&
                  groupedByProperty.map((propertyGroup) => {
                    const ranges = propertyGroup.contracts
                      .map((contract) =>
                        getBarPositionByYear(
                          contract.startDate,
                          contract.finishDate,
                          selectedYear
                        )
                      )
                      .filter(
                        (range) =>
                          range.startMonthIndex !== null && range.endMonthIndex !== null
                      ) as Array<Required<BarPosition>>;

                    const startMonthIndex =
                      ranges.length > 0
                        ? Math.min(...ranges.map((range) => range.startMonthIndex ?? 0))
                        : null;
                    const endMonthIndex =
                      ranges.length > 0
                        ? Math.max(...ranges.map((range) => range.endMonthIndex ?? 0))
                        : null;
                    const isExpanded = expandedPropertyId === propertyGroup.propertyId;

                    return [
                      <tr
                        key={`${propertyGroup.propertyId}-row`}
                        onClick={() =>
                          setExpandedPropertyId((current) =>
                            current === propertyGroup.propertyId
                              ? null
                              : propertyGroup.propertyId
                          )
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{propertyGroup.propertyId}</td>
                        <td>{propertyGroup.propertyName}</td>
                        <td>
                          <StatusPill>
                            {`${propertyGroup.contracts.length} ${
                              userLanguage === 'es' ? 'contratos' : 'contracts'
                            }`}
                          </StatusPill>
                        </td>
                        <MonthsCell colSpan={12}>
                          <MonthsBarTrack>
                            {months.map((_, index) => (
                              <MonthSlot key={index} />
                            ))}
                            {startMonthIndex != null &&
                              endMonthIndex != null &&
                              endMonthIndex >= startMonthIndex && (
                                <MonthBar $start={startMonthIndex} $end={endMonthIndex} />
                              )}
                          </MonthsBarTrack>
                        </MonthsCell>
                      </tr>,
                      isExpanded && (
                        <tr key={`${propertyGroup.propertyId}-details`}>
                          <td colSpan={3 + months.length}>
                            <SelectedContractPanel>
                              <SelectedTitle>
                                {userLanguage === 'es'
                                  ? 'Unidades de la propiedad'
                                  : 'Property units'}
                              </SelectedTitle>
                              <UnitsWrapper>
                                {propertyGroup.contracts.map((contract) => {
                                  const unitName =
                                    contract.unities?.map((unity) => unity.name).join(', ') ||
                                    (userLanguage === 'es' ? 'Sin unidad' : 'No unit');
                                  return (
                                    <UnitItem key={contract.id}>
                                      <UnitName>{unitName}</UnitName>
                                      <UnitPeriod>
                                        {`${formatDate(
                                          contract.startDate,
                                          userLanguage
                                        )} - ${formatDate(
                                          contract.finishDate,
                                          userLanguage
                                        )}`}
                                      </UnitPeriod>
                                    </UnitItem>
                                  );
                                })}
                              </UnitsWrapper>
                            </SelectedContractPanel>
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                {selectedYear !== null &&
                  viewType === 'unit' &&
                  items.map((contract) => {
                    const { startMonthIndex, endMonthIndex } = getBarPositionByYear(
                      contract.startDate,
                      contract.finishDate,
                      selectedYear
                    );
                    const unitName =
                      contract.unities?.map((unity) => unity.name).join(', ') ||
                      (userLanguage === 'es' ? 'Sin unidad' : 'No unit');

                    return (
                      <tr key={`${contract.id}-unit-row`}>
                        <td>{contract.property?.id ?? contract.id}</td>
                        <td>{`${contract.property?.name ?? '-'} / ${unitName}`}</td>
                        <td>
                          <StatusPill>
                            {`${formatDate(contract.startDate, userLanguage)} - ${formatDate(
                              contract.finishDate,
                              userLanguage
                            )}`}
                          </StatusPill>
                        </td>
                        <MonthsCell colSpan={12}>
                          <MonthsBarTrack>
                            {months.map((_, index) => (
                              <MonthSlot key={index} />
                            ))}
                            {startMonthIndex != null &&
                              endMonthIndex != null &&
                              endMonthIndex >= startMonthIndex && (
                                <MonthBar $start={startMonthIndex} $end={endMonthIndex} />
                              )}
                          </MonthsBarTrack>
                        </MonthsCell>
                      </tr>
                    );
                  })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={16}>{texts.noData.table}</td>
                  </tr>
                )}
              </tbody>
            </RoadmapTable>
          </TableWrapper>
        </Col>
      </Row>
      {showPagination && (
        <Row>
          <Col>
            <Pagination
              currentPage={currentPage}
              onPageChange={handlePageChange}
              currentDataCount={currentDataCount}
              pageSize={filters.pageSize || null}
              totalPages={totalPages}
            />
          </Col>
        </Row>
      )}
    </Container>
  );
};

